import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, CheckCircle2, ShieldCheck, Briefcase, UserPlus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const hrSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "HR & Recruitment",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Building Strong Teams for Lasting Success. Find the right talent fast with customized HR and recruitment solutions."
};

const HRRecruiting = () => {
  const { language } = useLanguage();
  const isRu = language === 'ru';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={isRu ? 'HR и Подбор персонала' : 'HR & Recruitment'}
        description={isRu ? 'Создание сильных команд для долгосрочного успеха' : 'Building Strong Teams for Lasting Success'}
        canonical="/hr-recruiting"
        schemaJson={hrSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp" 
              alt="HR & Recruitment" 
              className="w-full h-full object-cover object-center opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {isRu ? 'HR и Подбор персонала' : 'HR & Recruitment'}
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                {isRu ? 'Создание сильных команд для' : 'Building Strong Teams for'}{' '}
                <span className="text-primary">{isRu ? 'долгосрочного успеха' : 'Lasting Success'}</span>
              </h1>
              
              <div className="flex flex-col items-center gap-6 mb-10">
                <div className="flex items-center gap-4 text-white/60">
                  <span className="text-sm uppercase tracking-widest">{isRu ? 'В партнерстве с:' : 'in Partnership with:'}</span>
                  <img 
                    src="https://vibe.filesafe.space/1777392978847194987/attachments/a5490e3b-08fe-4af1-b09d-517d1f1f179d.png" 
                    alt="House of Trucking" 
                    className="h-16 w-auto bg-white px-4 py-2 rounded-lg"
                  />
                </div>
                
                <div className="bg-primary/10 border border-primary/20 p-6 rounded-2xl backdrop-blur-sm max-w-lg">
                  <h3 className="text-xl font-bold text-white mb-2">{isRu ? 'Нужны надежные водители?' : 'Need reliable drivers?'}</h3>
                  <p className="text-primary font-bold text-lg mb-4">{isRu ? 'Наймите водителя сейчас — платите позже!' : 'Hire driver now - pay later!'}</p>
                  <Link to="/free-consultation">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-12 w-full font-bold">
                      {isRu ? 'ОТПРАВИТЬ ЗАПРОС' : 'FILL UP YOUR REQUEST'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-slate-50 relative">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <UserPlus className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">{isRu ? 'Быстрый поиск талантов' : 'Find the Right Talent Fast'}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {isRu 
                    ? 'Поиск подходящих людей может быть сложным и трудоемким процессом. Наши услуги по подбору персонала упрощают этот процесс, соединяя вас с квалифицированными кандидатами, которые соответствуют вашей корпоративной культуре и целям.' 
                    : 'Hiring the right people can be challenging and time-consuming. Our HR & recruitment services streamline the hiring process, connecting you with qualified candidates who align with your company culture and goals.'}
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <Briefcase className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">{isRu ? 'Индивидуальные решения по найму' : 'Customized Hiring Solutions'}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {isRu 
                    ? 'Каждый бизнес уникален, как и его потребности в найме. Мы предлагаем индивидуальные решения по подбору персонала, адаптированные к вашей отрасли и размеру компании. От отбора кандидатов до проведения собеседований — мы разрабатываем стратегию найма, которая подходит именно вам.' 
                    : 'Every business is unique, and so are its hiring needs. We offer customized HR and recruitment solutions tailored to your industry and company size. From screening candidates to conducting interviews, we design a hiring strategy that fits your specific requirements.'}
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">{isRu ? 'Снижение текучести кадров' : 'Reduce Turnover, Increase Retention'}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {isRu 
                    ? 'Текучесть кадров может быть дорогостоящей и деструктивной. Наши стратегии подбора персонала сосредоточены на поиске кандидатов, которые не только обладают нужными навыками, но и готовы остаться в вашей компании надолго.' 
                    : 'Employee turnover can be costly and disruptive. Our recruitment strategies focus on finding candidates who not only have the right skills but are also likely to stay with your company long-term.'}
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">{isRu ? 'Соблюдение норм без стресса' : 'Stay Compliant and Stress-Free'}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {isRu 
                    ? 'Соблюдение норм трудового законодательства может быть сложной областью с постоянно меняющимися правилами. Наши услуги по управлению персоналом обеспечивают ваше соответствие стандартам, беря на себя всю бумажную работу.' 
                    : 'HR compliance can be a complex area to navigate, with ever-changing regulations and labor laws. Our HR services keep you compliant and up-to-date, handling all the paperwork and ensuring that your business adheres to legal standards.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner Section */}
        <section className="py-24 bg-navy-950 relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="relative rounded-[2.5rem] overflow-hidden min-h-[320px] md:min-h-[400px] group shadow-2xl border border-white/5 bg-navy-900">
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="https://vibe.filesafe.space/1777392978847194987/assets/eb8ee3aa-631a-4271-9ab5-0652cd7aa6c3.png" 
                  alt="HR & Recruitment" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-60" 
                />
                <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col md:flex-row items-start md:items-center justify-between p-8 md:p-16 gap-8">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary font-bold tracking-widest uppercase text-[10px] mb-6">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                    {isRu ? 'HR и Подбор персонала' : 'HR & Recruitment'}
                  </div>
                  <h3 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-4 leading-tight">
                    {isRu ? 'Соединяем вас с талантами для роста вашего бизнеса' : 'Connecting You with Talent to Fuel Your Growth'}
                  </h3>
                </div>

                <Link to="/free-consultation" className="shrink-0">
                  <Button 
                    size="lg"
                    className="group/btn relative overflow-hidden h-16 px-10 text-lg font-bold rounded-xl bg-primary text-white hover:bg-primary/90 shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all duration-300"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {isRu ? 'Узнать больше о найме' : 'Learn More about Recruiting'}
                      <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </span>
                    <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white/10 opacity-40 group-hover/btn:animate-shine"></div>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats & Hand Image Section */}
        <section className="py-24 bg-white overflow-hidden border-t border-slate-100">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-[1920px] mx-auto">
            <div className="lg:w-1/2 relative group w-full flex justify-start overflow-hidden lg:overflow-visible">
              <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <img 
                src="https://static.wixstatic.com/media/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png/v1/fill/w_960,h_706,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png" 
                alt="AITIM Capital Card" 
                className="w-full max-w-none lg:w-[110%] lg:-ml-12 drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)] group-hover:-translate-y-4 group-hover:scale-105 transition-all duration-700 relative z-10"
              />
            </div>
            <div className="lg:w-1/2 px-4 lg:pr-12 xl:pr-24 w-full">
              <div className="max-w-xl mx-auto lg:mx-0">
                <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-xs mb-6 tracking-widest uppercase">
                  {isRu ? 'Почему выбирают нас' : 'Why Choose Us'}
                </div>
                <h2 className="text-5xl md:text-7xl font-black text-navy-950 mb-16 tracking-tight">AITIM GROUP</h2>
                
                <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-8 sm:gap-y-12">
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">10+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'ЛЕТ ОПЫТА' : 'YEARS OF\nEXPERIENCE'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">7000+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'ГРУЗОВИКОВ В СЕТИ' : 'TRUCKS IN OUR\nNETWORK'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">100%</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'УДОВЛЕТВОРЕННОСТЬ' : 'CUSTOMER\nSATISFACTION'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">20+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'НАГРАДЫ' : 'AWARDS AND\nRECOGNITIONS'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default HRRecruiting;
